import * as fabric from "fabric";
import { handleDisplayToolbar } from "../../components/utils";
import store from "../../store";


/**
 * Calculates the position and dimensions of an item on a fabric.js canvas based on its CSS styles and the canvas
 * scale.
 *
 * @param itemImage
 * @param {number} scale - The scale factor to apply to the item's dimensions.
 * @param {React.MutableRefObject<fabric.Canvas | null>} fabricCanvasInstanceRef - A reference to the fabric.js canvas
 *   instance.
 * @returns {object} An object containing the calculated horizontal position, vertical position, width, and height of
 *   the item.
 */
export function calculatePosition(
  itemImage,
  scale: number,
  fabricCanvasInstanceRef: React.MutableRefObject<fabric.Canvas | null>
) {

  if ( !fabricCanvasInstanceRef.current ) {
    throw new Error( "Canvas instance is not initialized." );
  }

  const canvasWidth = fabricCanvasInstanceRef.current.width || 0;
  const canvasHeight = fabricCanvasInstanceRef.current.height || 0;

  const itemImageWidth = itemImage.style.width
                         ?
                         parseFloat( itemImage.style.width ) * scale
                         :
                         (canvasWidth - (parseFloat( itemImage.style.left ) * scale) - (parseFloat( itemImage.style.right ) * scale));

  const itemImageHeight = itemImage.style.height
                          ?
                          parseFloat( itemImage.style.height ) * scale
                          :
                          (canvasHeight - (parseFloat( itemImage.style.top ) * scale) - (parseFloat( itemImage.style.bottom ) * scale));

  const hPosition = itemImage.style.left
                    ? (parseFloat( itemImage.style.left ) * scale) + (itemImageWidth / 2)
                    : (canvasWidth) - (parseFloat( itemImage.style.right ) * scale) - (itemImageWidth / 2);

  const vPosition = itemImage.style.top
                    ? (parseFloat( itemImage.style.top ) * scale) + (itemImageHeight / 2)
                    : (canvasHeight) - (parseFloat( itemImage.style.bottom ) * scale) - (itemImageHeight / 2);

  return { hPosition, vPosition, width: itemImageWidth, height: itemImageHeight };
}

/**
 * Opens a file upload dialog for uploading an image and updates the toolbar display for the selected fabric object.
 *
 * @param {Object} fabricObject - The fabric object representing the current selection in the canvas, including its
 *   properties such as `idx`.
 * @param {Function} dispatch - A function to dispatch actions to update application state.
 * @returns {Function} A function that handles the file upload dialog opening when triggered by an event.
 */
export const openUploadImageBox = (
  fabricObject,
  dispatch
) => {
  return ( event: any ) => {
    event.e.stopPropagation();
    handleDisplayToolbar( dispatch, "image", fabricObject.idx );

    setTimeout( () => {
      const uploadButton = document.querySelector( "#file-upload" ) as HTMLInputElement;
      if ( uploadButton ) {
        uploadButton.value = "";
        uploadButton.click();
      }
    }, 50 );
  };
};

/**
 * Determines if the given pointer coordinates are inside the bounds of the specified clip rectangle.
 *
 * @param {Object} pointer An object representing the pointer's position, with `x` and `y` properties indicating the
 *   coordinates.
 * @param {Object} clipRect A rectangle object with a `getBoundingRect` method that returns the rectangle's boundaries
 *   as an object containing `left`, `top`, `width`, and `height` properties.
 * @return {boolean} Returns `true` if the pointer coordinates are within the clip rectangle's bounds; otherwise,
 *   returns `false`.
 */
export function checkIfInsideClipRect( pointer: { x: number; y: number; },
  clipRect: { getBoundingRect: () => any; } ): boolean {
  const clipBounds = clipRect.getBoundingRect();
  return (
    pointer.x >= clipBounds.left &&
    pointer.x <= clipBounds.left + clipBounds.width &&
    pointer.y >= clipBounds.top &&
    pointer.y <= clipBounds.top + clipBounds.height
  );
}

/**
 * Creates a custom rectangular path with specified corner radii.
 *
 * @param {number} width - The width of the rectangle.
 * @param {number} height - The height of the rectangle.
 * @param {Object} radii - An object defining the corner radii.
 * @param {number} radii.topLeft - The radius of the top-left corner.
 * @param {number} radii.topRight - The radius of the top-right corner.
 * @param {number} radii.bottomLeft - The radius of the bottom-left corner.
 * @param {number} radii.bottomRight - The radius of the bottom-right corner.
 * @return {string} The SVG path data string for the custom rectangular path.
 */
export function createCustomRectPath( width: number,
  height: number,
  radii: { topLeft: number; topRight: number; bottomLeft: number; bottomRight: number; } ): string {
  const { topLeft, topRight, bottomLeft, bottomRight } = radii;

  return `
      M ${ topLeft } 0
      L ${ width - topRight } 0
      Q ${ width } 0 ${ width } ${ topRight }
      L ${ width } ${ height - bottomRight }
      Q ${ width } ${ height } ${ width - bottomRight } ${ height }
      L ${ bottomLeft } ${ height }
      Q 0 ${ height } 0 ${ height - bottomLeft }
      L 0 ${ topLeft }
      Q 0 0 ${ topLeft } 0
      Z
    `;
}

/**
 * Retrieves the rotation angle of an element in degrees.
 *
 * @function
 * @returns {number} The rotation angle in degrees. Returns 0 if no rotation is found.
 */
export const getRotation = ( item ) => {
  const transformMatch = item.style.transform?.match( /rotate\(([-\d.]+)deg\)/ );
  return transformMatch ? parseFloat( transformMatch[ 1 ] ) : 0;
};

/**
 * Creates a rectangular border around a given clipping rectangle.
 *
 * @param {Object} clipRect - The clipping rectangle object containing dimensions like width and height.
 * @param {number} borderWidth - The width of the border to be added to the clipping rectangle.
 * @param {string} borderColor - The color of the border.
 * @param containerRotation
 * @param radius
 * @param hPosition
 * @param vPosition
 * @returns {fabric.Rect} A new fabric.Rect object representing the border rectangle with the specified properties.
 */
export const createBorderRect = ( clipRect: any,
  borderWidth: number,
  borderColor: string,
  containerRotation: number,
  radius: number,
  hPosition: number,
  vPosition: number ): fabric.Rect => {
  return new fabric.Rect( {
    left: hPosition,
    top: vPosition,
    width: clipRect.width + borderWidth * 2,
    height: clipRect.height + borderWidth * 2,
    originX: "center",
    originY: "center",
    absolutePositioned: true,
    fill: borderColor,
    angle: containerRotation,
    rx: radius,
    ry: radius,
    selectable: false,
    evented: false
  } );
};

/**
 * Parses a given border-radius style value and calculates the scaled value.
 *
 * @param {string | undefined} styleValue - The CSS border-radius value as a string or undefined.
 * @returns {number} The scaled border-radius value or 0 if the input is undefined.
 */
export const parseBorderRadius = ( styleValue: string | undefined, scale ): number =>
  styleValue ? parseFloat( styleValue ) * scale : 0;

/**
 * Generates the base configuration object for a shape to be rendered.
 * The configuration includes properties such as position, stroke, fill,
 * interactivity, rotation, and dimensions.
 *
 * @return {Object} The configuration object for the shape, containing properties for positioning, styling, and
 *   behavior.
 */
export function getBaseShapeConfig( containerRotation, clipRect ) {

  return {
    absolutePositioned: true,
    stroke: process.env.STROKE_COLOR,
    strokeWidth: Number( process.env.STROKE_WIDTH ),
    strokeDashArray: [ 10, 5 ],
    fill: "transparent",
    selectable: false,
    evented: false,
    angle: containerRotation,
    originX: clipRect.originX,
    originY: clipRect.originY,
    rx: clipRect.rx,
    ry: clipRect.ry
  };
}

/**
 * Adds a clipping path to a given fabric.js image object.
 *
 * @param {Object} fabricImage - The fabric.js image object to which the clipping path will be added.
 * @param {Object} clipShape - The shape or object to be used as the clipping path.
 * @return {void}
 */
export function addClipPath( fabricImage, clipShape ) {
  fabricImage.clipPath = clipShape;
}

/**
 * Updates the border radius properties of clip and selected rectangles based on the selected layout.
 *
 * @param {Object} clipRect - The clipping rectangle object to update
 * @param {Object} selectedRect - The selected rectangle object to update
 * @param {string|number} selectedLayout - The ID of the selected layout to get border radius from
 * @returns {void}
 *
 * @example
 * // Update radius properties for clip and selected rectangles
 * updateRadiusProperties(clipRect, selectedRect, "layout1");
 */
export function updateRadiusProperties( clipRect, selectedRect, selectedLayout, radius ) {
  const layouts = store.getState().editor.currentData.layoutData;
  const layout = layouts.find( ( layout ) => layout.id === selectedLayout );
  const scale = store.getState().editor.scale;

  const newRadius = layout && layout.borderRadius ? layout.borderRadius * scale : 0;
  if ( radius < 100 ) {

    selectedRect.set( { rx: newRadius, ry: newRadius } );
    clipRect.set( { rx: newRadius, ry: newRadius } );
  }
}
